import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const DataTable = ({ columns, data, searchable = true }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    // Filter data
    const filteredData = data.filter(row => {
        if (!searchTerm) return true;
        
        // If it's a long number (likely a barcode scan), use last 5 digits
        let searchQ = searchTerm.trim().toLowerCase();
        if (/^\d{6,}$/.test(searchQ)) {
            searchQ = searchQ.slice(-5);
        }

        return Object.values(row).some(val =>
            String(val).toLowerCase().includes(searchQ)
        );
    });

    // Pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    return (
        <div className="erp-card overflow-hidden">
            {searchable && (
                <div className="p-4 border-b border-erp-border bg-erp-surface-2">
                    <input
                        type="text"
                        placeholder={t('Search records...')}
                        className="erp-input w-full md:w-72"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // Reset to first page
                        }}
                    />
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="erp-table">
                    <thead>
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx}>{col.header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, rIdx) => (
                                <tr key={rIdx}>
                                    {columns.map((col, cIdx) => (
                                        <td key={cIdx}>
                                            {col.render ? col.render({ ...row, _rowIndex: rIdx }) : row[col.accessor]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="!text-center !py-10 text-erp-text-3 text-sm">
                                    {t('No records found.')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-erp-border flex items-center justify-between bg-erp-surface-2">
                    <span className="text-sm text-erp-text-3">
                        {t('Showing')} {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, filteredData.length)} {t('of')} {filteredData.length}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                            className="btn-secondary py-1 text-xs disabled:opacity-40"
                        >
                            {t('Prev')}
                        </button>
                        <span className="text-sm text-erp-text-2 font-medium">{currentPage} / {totalPages}</span>
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                            className="btn-secondary py-1 text-xs disabled:opacity-40"
                        >
                            {t('Next')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
